import { useLayoutEffect, useRef, useState } from 'react';
import { Flex, Text, type TextProps } from '@mantine/core';
import { SINGLE_LINE_STYLE } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/singleLine.ts';
import {
	getLinkedNamesParts,
	MAX_VISIBLE_LINKED_NAMES,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/investorDocuments.utils.ts';

interface Props {
	names: string[];
	suffix?: string;
	textProps?: TextProps;
}

const FIXED_STYLE = { ...SINGLE_LINE_STYLE, flexShrink: 0, marginLeft: '0.25em' };

export const LinkedToNames = ({ names, suffix, textProps }: Props) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const measureRef = useRef<HTMLParagraphElement>(null);
	const [visibleCount, setVisibleCount] = useState(MAX_VISIBLE_LINKED_NAMES);

	const preferred = getLinkedNamesParts(names, MAX_VISIBLE_LINKED_NAMES);
	const preferredLine = [
		preferred.visible,
		preferred.hiddenCount > 0 ? `(+${preferred.hiddenCount})` : '',
		suffix?.trim() ?? '',
	]
		.filter(Boolean)
		.join(' ');
	const canCollapse = names.length > MAX_VISIBLE_LINKED_NAMES;

	useLayoutEffect(() => {
		const container = containerRef.current;
		const measure = measureRef.current;

		if (!container || !measure) {
			setVisibleCount(MAX_VISIBLE_LINKED_NAMES);
			return;
		}

		const update = () =>
			setVisibleCount(
				measure.scrollWidth > container.clientWidth ? 1 : MAX_VISIBLE_LINKED_NAMES
			);

		update();

		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		const observer = new ResizeObserver(update);
		observer.observe(container);

		return () => observer.disconnect();
	}, [canCollapse, preferredLine]);

	const { visible, hiddenCount } = getLinkedNamesParts(names, visibleCount);

	return (
		<Flex
			ref={containerRef}
			wrap="nowrap"
			miw={0}
			w="100%"
			pos="relative"
			title={names.join(', ')}
		>
			<Text {...textProps} truncate style={SINGLE_LINE_STYLE}>
				{visible}
			</Text>

			{hiddenCount > 0 && (
				<Text {...textProps} style={FIXED_STYLE}>
					(+{hiddenCount})
				</Text>
			)}

			{!!suffix?.trim() && (
				<Text {...textProps} style={FIXED_STYLE}>
					{suffix.trim()}
				</Text>
			)}

			{canCollapse && (
				<Text
					ref={measureRef}
					{...textProps}
					aria-hidden
					style={{
						...SINGLE_LINE_STYLE,
						position: 'absolute',
						visibility: 'hidden',
						pointerEvents: 'none',
					}}
				>
					{preferredLine}
				</Text>
			)}
		</Flex>
	);
};
